import { InfoCircleOutlined } from '@ant-design/icons'
import { Link, navigate } from '@reach/router'
import { Avatar, Button, Checkbox, Col, Form, Input, message, Modal, Radio, Row, Tooltip } from 'antd'
import moment from 'moment'
import React, { useContext, useRef, useState } from 'react'
import styled from 'styled-components'
import { useLoading, useMedia } from '../common/hooks'
import { passwordRule, phraseRule, required, usernameRule, validateMessages } from '../common/validate'
import { RegisterContext } from '../pages'
import { anoVerify, registerSendSMS, registerSMSVerify, registerSubmit, registerUsernameVerify } from '../services'
import { terms } from './terms'
import { ErrorBanner } from './ui'
import Avatar1 from '../assets/avatar-1.png'
import Avatar2 from '../assets/avatar-2.png'
import Avatar3 from '../assets/avatar-3.png'
import Avatar4 from '../assets/avatar-4.png'
import Avatar5 from '../assets/avatar-5.png'
import Avatar6 from '../assets/avatar-6.png'
import Avatar7 from '../assets/avatar-7.png'
import Avatar8 from '../assets/avatar-8.png'
import { preventEnterKey } from '../common/helper'
import { ROOT_PATH, SMS_EXPIRING } from '../common/config'
import { FormProps } from 'antd/lib/form'

enum RStep {
  /** step 1 */
  EnterNumber = 0,
  /** step 2 */
  Agreement,
  /** step 3 */
  OTPCheck,
  /** step 4 */
  CreateUserName,
  /** step 5 */
  CreatePassword,
  /** step 6 */
  ChoosePhrase,
}

const StyledRegisterForm = styled.div`
  height: calc(var(--vh) * 100);
  padding: 0 120px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;

  .ant-form-item-control-input-content {
    max-width: 90%;
  }

  h1 {
    font-weight: 500;
    font-size: 32px;
    line-height: 38px;
    color: #262626;
    margin-top: 120px;
    margin-bottom: 100px;
    text-align: center;
  }

  @media screen and (max-width: 1350px) {
    padding: 0 20px;
    h1 {
      margin-bottom: 35px;
      margin-top: 45px;
    }
  }
  .next {
    width: 180px;
    margin-bottom: 78px;
  }

  form {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    /* padding-bottom: 120px; */

    #image {
      margin: 0 -10px;
    }
  }

  .register-phase {
    h1 {
      margin-top: 100px;
      margin-bottom: 25px;
      font-size: 30px;
    }
  }
  @media screen and (max-width: 1350px) {
    .register-phase {
      h1 {
        margin-top: 55px;
        margin-bottom: 25px;
        font-size: 28px;
      }
    }
  }

  .ant-form-item {
    max-width: 500px;
  }

  .ant-form-item-children-icon {
    margin-right: -15px !important;

    .anticon-close-circle {
      display: none;
    }
  }

  .footer-btn {
    margin-top: auto;
  }

  .ant-avatar {
    margin: 10px 10px;
    cursor: pointer;
    box-sizing: border-box;
  }

  .ant-radio {
    display: none;
  }

  .ant-radio-wrapper-checked .ant-avatar {
    border: 5px solid #fff;
  }

  .ant-form-item-explain-error {
    font-weight: bold;
  }

  .ant-form-item-has-success.ant-form-item-has-feedback .ant-form-item-children-icon {
    color: #187c6a;
    font-size: 20px;
  }

  @media screen and (max-width: 600px) {
    .image-radio {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      &::after {
        content: '';
        width: 106px;
      }
    }

    h1 {
      margin-top: 100px;
    }

    .register-phase .footer-btn {
      margin-bottom: -20px;
    }
  }
  @media screen and (max-width: 1000px) {
    .next {
      width: 120px;
    }
  }
`

const FooterButtons: React.FC<{ loading?: boolean; last?: boolean; errMsg?: string; nextText?: string; restart?: boolean }> = (props) => {
  const { state, dispatch } = useContext(RegisterContext)
  const back = () => {
    dispatch({ type: 'ChangeStep', payload: state.step - 1 })
  }
  let lastBtnText = props.last && !props.nextText ? 'Register' : 'Next'
  if (props.nextText) lastBtnText = props.nextText

  return (
    <>
      <Row className="footer-btn" align="middle" justify="center">
        {state.step === 0 ? null : (
          <Button className="next mr-auto" onClick={back} size="large" type="primary" danger>
            Back
          </Button>
        )}
        {props.restart ? (
          <Link to={ROOT_PATH}>
            <Button className="next" loading={props.loading} size="large" type="primary">
              Cancel
            </Button>
          </Link>
        ) : (
          <Button className="next" loading={props.loading} size="large" type="primary" htmlType="submit">
            {lastBtnText}
          </Button>
        )}
      </Row>
      {props.errMsg && <ErrorBanner>{props.errMsg}</ErrorBanner>}
    </>
  )
}

const EnterNumber = () => {
  const { dispatch } = useContext(RegisterContext)
  const { loading, start, finish } = useLoading()
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined)
  const isMobile = useMedia('(max-width: 600px)')
  const onFinish = async (values: { ano: string }) => {
    console.log(values)
    if (values.ano.length !== 12) {
      setErrMsg('Loan agreement entered is invalid. Kindly check and re-enter.')
      return
    }
    start()
    setErrMsg(undefined)
    try {
      await anoVerify({ ano: values.ano })
      finish()
      dispatch({ type: 'ApplyForm', payload: { ano: values.ano } })
      dispatch({ type: 'ChangeStep', payload: RStep.Agreement })
      setErrMsg(undefined)
    } catch (error) {
      setErrMsg(error.message)
      finish()
      return
    }
  }
  return (
    <Form layout={isMobile ? 'horizontal' : 'vertical'} requiredMark={false} validateMessages={validateMessages} onFinish={onFinish}>
      <h1>Enter Your Loan Agreement Number </h1>
      <Form.Item name="ano" label="Loan Agreement Number" rules={[required]}>
        <Input
          size="large"
          placeholder="Enter your Loan Agreement Number"
          onKeyDown={preventEnterKey}
          suffix={
            <Tooltip title="It is the twelve (12) digit unique number given to your loan. It can be found in your loan agreement that was posted to you.">
              <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
            </Tooltip>
          }
        />
      </Form.Item>
      <FooterButtons loading={loading} errMsg={errMsg} />
    </Form>
  )
}
const Agreement = () => {
  const { dispatch } = useContext(RegisterContext)
  const isMobile = useMedia('(max-width: 600px)')
  const onFinish = () => {
    dispatch({ type: 'ChangeStep', payload: RStep.OTPCheck })
  }

  return (
    <Form layout={isMobile ? 'horizontal' : 'vertical'} onFinish={onFinish}>
      <h1 style={{ marginBottom: '1em' }}>Terms & Conditions</h1>
      <article className="h-5/6 overflow-y-scroll bg-white p-5 mb-5" dangerouslySetInnerHTML={{ __html: terms }} />
      <Form.Item
        name="terms"
        valuePropName="checked"
        rules={[
          {
            validator: (_, value) => (value ? Promise.resolve() : Promise.reject('Kindly accept the terms & conditions to proceed.')),
          },
        ]}
      >
        <Checkbox>I have read and understood the terms & conditions</Checkbox>
      </Form.Item>
      <FooterButtons nextText="Yes, I accept" />
    </Form>
  )
}

const OTPCheck = () => {
  const { state, dispatch } = useContext(RegisterContext)
  const { loading, start, finish } = useLoading()
  const { loading: submiting, start: submit, finish: done } = useLoading()
  const [smsSended, setSmsSended] = useState(false)
  const [remaining, setRemaining] = useState(SMS_EXPIRING)
  const sec = useRef(SMS_EXPIRING)
  const intervalRef = useRef<number | null>(0)
  const [errMsg, setErrMsg] = useState<string | undefined>()
  const isMobile = useMedia('(max-width: 600px)')

  const startInterval = () => {
    intervalRef.current = setInterval(() => {
      if (sec.current <= 0) {
        resetInterval()
      } else {
        setRemaining((s) => s - 1)
        sec.current -= 1
      }
    }, 1000)
  }
  const resetInterval = () => {
    sec.current = SMS_EXPIRING
    setRemaining(SMS_EXPIRING)
    console.log('sec', sec.current, 'remain', remaining)
    setSmsSended(false)
    clearInterval(intervalRef.current as number)
    intervalRef.current = null
  }

  const sendSMS = async () => {
    if (smsSended) return
    start()
    setErrMsg(undefined)
    try {
      const { regId, email } = await registerSendSMS({ ano: state.ano })
      dispatch({ type: 'ApplyForm', payload: { regId, email } })
      setSmsSended(true)
      startInterval()
      setErrMsg(undefined)
    } catch (error) {
      // message.error('send sms failed: ' + error.message)
      setErrMsg('send sms failed: ' + error.message)
    }
    finish()
  }
  const onFinish = async ({ sms }: { sms: string }) => {
    submit()
    setErrMsg(undefined)
    try {
      if (!sms.trim().length) {
        message.error('No OTP has been requested. Kindly request an OTP first.')
        return
      }
      await registerSMSVerify({ sms, regId: state.regId })
      setErrMsg(undefined)
      done()
      dispatch({ type: 'ChangeStep', payload: RStep.CreateUserName })
    } catch (error) {
      setErrMsg(error.message)
      done()
    }
  }
  return (
    <Form layout={isMobile ? 'horizontal' : 'vertical'} requiredMark={false} validateMessages={validateMessages} onFinish={onFinish}>
      <h1>One Time Password Verification (OTP)</h1>
      <Form.Item label="Enter One Time Password" name="sms" rules={[required]}>
        <Row align="middle" justify="space-between" wrap={false}>
          <Col xs="12" sm="18">
            <Input
              size="large"
              placeholder="Enter your OTP"
              suffix={
                <Tooltip title="It is a unique 6 digit code which will be sent to your hand phone number in our record via SMS. ">
                  <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                </Tooltip>
              }
            />
          </Col>
          <Col xs="12" sm="6">
            <Button type="link" loading={loading} onClick={sendSMS}>
              {smsSended ? `expiring ${moment.utc(remaining * 60 * 1000).format('HH:mm')}` : 'Request OTP'}
            </Button>
          </Col>
        </Row>
      </Form.Item>
      <FooterButtons loading={submiting} errMsg={errMsg} />
    </Form>
  )
}

const CreateUserName = () => {
  const { dispatch } = useContext(RegisterContext)
  const { loading, start, finish } = useLoading()
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined)
  const isMobile = useMedia('(max-width: 600px)')
  const onFinish = async ({ usrId }: { usrId: string }) => {
    setErrMsg(undefined)
    start()
    try {
      await registerUsernameVerify(usrId)
      dispatch({ type: 'ApplyForm', payload: { usrId } })
      dispatch({ type: 'ChangeStep', payload: RStep.CreatePassword })
      setErrMsg(undefined)
    } catch (error) {
      console.log(error)
      setErrMsg(error.message)
    }
    finish()
  }

  return (
    <Form layout={isMobile ? 'horizontal' : 'vertical'} requiredMark={false} validateMessages={validateMessages} onFinish={onFinish}>
      <h1>Create Your Username </h1>
      <Form.Item name="usrId" label="Username" help="" rules={[usernameRule]} hasFeedback>
        <Input
          size="large"
          placeholder="Enter your username"
          suffix={
            <Tooltip title="Create a unique user name to login to the Portal. The user name must be from 8 to 30 alphanumeric character long. The user name can contain special symbol; such as @ / - _ ? # $ . \ ">
              <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
            </Tooltip>
          }
        />
      </Form.Item>
      <FooterButtons loading={loading} errMsg={errMsg} />
    </Form>
  )
}

const CreatePassword = () => {
  const { dispatch, state } = useContext(RegisterContext)
  const [errMsg, setErrMsg] = useState('')
  const isMobile = useMedia('(max-width: 600px)')
  const onFinish = ({ password, password2, email }: Record<string, string>) => {
    setErrMsg('')
    dispatch({ type: 'ApplyForm', payload: { pwd: password, email: email || state.email } })
    dispatch({ type: 'ChangeStep', payload: RStep.ChoosePhrase })
  }

  const showErrorMsg: FormProps['onFinishFailed'] = ({ errorFields }) => {
    if (errorFields.length) {
      setErrMsg(errorFields.map((item) => [...item.errors]).join('\n'))
    } else {
      setErrMsg('')
    }
  }

  return (
    <Form
      layout={isMobile ? 'horizontal' : 'vertical'}
      requiredMark={false}
      onFinishFailed={showErrorMsg}
      validateMessages={validateMessages}
      onFinish={onFinish}
      initialValues={{ email: state.email }}
    >
      <h1>Create Your Password </h1>
      <Form.Item name="password" label="Password" rules={[passwordRule]} help="" hasFeedback>
        <Row align="middle" gutter={1} justify="space-between">
          <Col span={20}>
            <Input.Password size="large" type="password" />
          </Col>
          <Col span={3}>
            <Tooltip title="Your password should be at least 8 characters long and should contain at least one number, one alphabet and one special characters of !@#%&?$.">
              <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
            </Tooltip>
          </Col>
        </Row>
      </Form.Item>
      <Form.Item
        name="password2"
        label="Reenter Password"
        help=""
        hasFeedback
        rules={[
          passwordRule,
          ({ getFieldValue }) => ({
            validator(rule, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve()
              }
              return Promise.reject('The two passwords that you entered do not match!')
            },
            validateTrigger: 'blur',
          }),
        ]}
      >
        <Row align="middle" gutter={1} justify="space-between">
          <Col span={20}>
            <Input.Password size="large" type="password" />
          </Col>
          <Col span={3}></Col>
        </Row>
      </Form.Item>
      <Form.Item
        label="Email Address"
        name="email"
        help=""
        hasFeedback
        rules={[
          {
            type: 'email',
            message: 'email is not valid',
          },
          {
            required: true,
            validator: (rule, value, cb) => {
              const valid = value || (!value && state.email)
              cb(valid ? undefined : 'email is required')
            },
          },
        ]}
      >
        <Input size="large" type="email" autoComplete="false" autoCorrect="false" />
      </Form.Item>
      <FooterButtons errMsg={errMsg} />
    </Form>
  )
}

const ChoosePhrase = () => {
  const { state } = useContext(RegisterContext)
  const { loading, start, finish } = useLoading()
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined)
  const [restart, setRestart] = useState<boolean>(false)
  const isMobile = useMedia('(max-width: 600px)')
  const onFinish = async ({ image, scrt }: { image: string; scrt: string }) => {
    setErrMsg(undefined)
    try {
      start()
      const { usrId, pwd, regId, email } = state
      await registerSubmit({ usrId, pwd, regId, email, image, scrt })
      Modal.success({
        maskClosable: false,
        title: 'Success',
        content: 'Welcome, you have been successfully registered ! Click OK to login to SHENMA Credit.',
        onOk: () => navigate(ROOT_PATH),
      })
      setErrMsg(undefined)
    } catch (error) {
      console.log('register', error)

      if (error && error.code && error.message) {
        const { code } = error
        if (Number(code) === 408) {
          setRestart(true)
        }
      }
      finish()
      setErrMsg('submit error: ' + error.message)
      // message.error('submit error: ' + error.message)
    }
  }
  const avatarSize = isMobile ? 50 : 60
  return (
    <Form className="register-phase" layout={isMobile ? 'horizontal' : 'vertical'} requiredMark={false} validateMessages={validateMessages} onFinish={onFinish}>
      <h1>Choose Your Personal Image & Security Phrase</h1>
      <Form.Item name="image" label="Personal Image" rules={[required]}>
        <Radio.Group className="image-radio">
          <Radio value={1}>
            <Avatar size={avatarSize} src={Avatar1} />
          </Radio>
          <Radio value={2}>
            <Avatar size={avatarSize} src={Avatar2} />
          </Radio>
          <Radio value={3}>
            <Avatar size={avatarSize} src={Avatar3} />
          </Radio>
          <Radio value={4}>
            <Avatar size={avatarSize} src={Avatar4} />
          </Radio>
          <Radio value={5}>
            <Avatar size={avatarSize} src={Avatar5} />
          </Radio>
          <Radio value={6}>
            <Avatar size={avatarSize} src={Avatar6} />
          </Radio>
          <Radio value={7}>
            <Avatar size={avatarSize} src={Avatar7} />
          </Radio>
          <Radio value={8}>
            <Avatar size={avatarSize} src={Avatar8} />
          </Radio>
        </Radio.Group>
      </Form.Item>
      <Form.Item
        label="Security phrase"
        name="scrt"
        rules={[phraseRule]}
        extra="Is a word(s) personally created by users for their identification of valid Shenma Customer Portal at login page."
      >
        <Input size="large" placeholder="Enter Your Security Phrase" />
        {/* suffix={
            <Tooltip title="Is a word(s) uniquely create by to login to the Portal. It should consist at least  3 to 30 characters.">
              <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
            </Tooltip>
          } */}
      </Form.Item>
      <FooterButtons last loading={loading} errMsg={errMsg} restart={restart} />
    </Form>
  )
}

const Steps = {
  [RStep.EnterNumber]: EnterNumber,
  [RStep.Agreement]: Agreement,
  [RStep.OTPCheck]: OTPCheck,
  [RStep.CreateUserName]: CreateUserName,
  [RStep.CreatePassword]: CreatePassword,
  [RStep.ChoosePhrase]: ChoosePhrase,
}

export const RegisterForm: React.FC = () => {
  const { state } = useContext(RegisterContext)
  const FormStep = Steps[state.step]
  return (
    <StyledRegisterForm>
      <FormStep />
    </StyledRegisterForm>
  )
}
